/**
 * File System Package Scanner
 * 
 * Infrastructure adapter implementing package scanning using file system operations.
 * Analyzes package structure, patterns, and architectural compliance.
 */

import { injectable } from 'inversify';
import { promises as fs } from 'fs';
import * as path from 'path';
import { IPackageScanner } from '../../application/services/BoundedContextAnalysisService';

@injectable()
export class FileSystemPackageScanner implements IPackageScanner {
  
  async scanPackage(packagePath: string): Promise<{
    packageName: string;
    contextName: string;
    contextType: 'core' | 'supporting' | 'generic';
    modulePatternType: 'createXModule' | 'static-configure' | 'no-module';
    hasSymbolConstants: boolean;
    symbolConstantName?: string;
    architecturalPatterns: {
      hasCleanArchitecture: boolean;
      hasPortsAndAdapters: boolean;
      hasCQRS: boolean;
      hasEventSourcing: boolean;
      hasDomainEvents: boolean;
      hasAntiCorruptionLayer: boolean;
    };
    dependencies: string[];
  }> {
    try {
      const packageJsonPath = path.join(packagePath, 'package.json');
      const packageJson = JSON.parse(await fs.readFile(packageJsonPath, 'utf-8'));
      
      const packageName = packageJson.name || path.basename(packagePath);
      const contextName = this.extractContextName(packageName);
      const contextType = this.determineContextType(packageName);
      
      // Scan for module patterns
      const modulePattern = await this.scanModulePattern(packagePath);
      const symbolInfo = await this.scanSymbolConstants(packagePath);
      const archPatterns = await this.scanArchitecturalPatterns(packagePath);
      const dependencies = this.extractDependencies(packageJson);
      
      return {
        packageName,
        contextName,
        contextType,
        modulePatternType: modulePattern,
        hasSymbolConstants: symbolInfo.hasSymbols,
        symbolConstantName: symbolInfo.symbolConstantName,
        architecturalPatterns: archPatterns,
        dependencies,
      };
    } catch (error) {
      throw new Error(`Failed to scan package ${packagePath}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async scanMonorepo(monorepoPath: string): Promise<string[]> {
    try {
      const packagesPath = path.join(monorepoPath, 'packages');
      const appsPath = path.join(monorepoPath, 'apps');
      
      const packages: string[] = [];
      
      // Scan packages directory
      if (await this.directoryExists(packagesPath)) {
        const packageDirs = await fs.readdir(packagesPath);
        for (const dir of packageDirs) {
          const fullPath = path.join(packagesPath, dir);
          if (await this.isPackageDirectory(fullPath)) {
            packages.push(fullPath);
          }
        }
      }
      
      // Scan apps directory
      if (await this.directoryExists(appsPath)) {
        const appDirs = await fs.readdir(appsPath);
        for (const dir of appDirs) {
          const fullPath = path.join(appsPath, dir);
          if (await this.isPackageDirectory(fullPath)) {
            packages.push(fullPath);
          }
        }
      }
      
      return packages;
    } catch (error) {
      throw new Error(`Failed to scan monorepo ${monorepoPath}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private extractContextName(packageName: string): string {
    // Extract context name from package name
    // e.g., "@swoft/party-manager" -> "PartyManager"
    const parts = packageName.split('/');
    const name = parts[parts.length - 1];
    return name.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('');
  }

  private determineContextType(packageName: string): 'core' | 'supporting' | 'generic' {
    const corePackages = [
      'party-manager', 'business-strategy', 'product-design-domain',
      'gtd-domain', 'ddd', 'ui-ux-design-domain'
    ];
    
    const supportingPackages = [
      'dev-ops', 'mongodb-toolkit', 'core-architecture', 'core-errors'
    ];
    
    if (corePackages.some(pkg => packageName.includes(pkg))) {
      return 'core';
    }
    
    if (supportingPackages.some(pkg => packageName.includes(pkg))) {
      return 'supporting';
    }
    
    return 'generic';
  }

  private async scanModulePattern(packagePath: string): Promise<'createXModule' | 'static-configure' | 'no-module'> {
    try {
      // Look for DI module files
      const possiblePaths = [
        'src/infrastructure/di',
        'src/di',
        'src/infrastructure',
        'src'
      ];
      
      for (const subPath of possiblePaths) {
        const fullPath = path.join(packagePath, subPath);
        if (await this.directoryExists(fullPath)) {
          const files = await fs.readdir(fullPath);
          
          for (const file of files) {
            if (file.endsWith('ContainerModule.ts') || file.endsWith('Module.ts')) {
              const filePath = path.join(fullPath, file);
              const content = await fs.readFile(filePath, 'utf-8');
              
              if (content.includes('createXModule') || content.includes('create') && content.includes('Module')) {
                return 'createXModule';
              }
              
              if (content.includes('ContainerModule')) {
                return 'static-configure';
              }
            }
          }
        }
      }
      
      return 'no-module';
    } catch (error) {
      return 'no-module';
    }
  }

  private async scanSymbolConstants(packagePath: string): Promise<{ hasSymbols: boolean; symbolConstantName?: string }> {
    try {
      // Look for symbol constants
      const possiblePaths = [
        'src/infrastructure/di',
        'src/di',
        'src/infrastructure',
        'src'
      ];
      
      for (const subPath of possiblePaths) {
        const fullPath = path.join(packagePath, subPath);
        if (await this.directoryExists(fullPath)) {
          const files = await fs.readdir(fullPath);
          
          for (const file of files) {
            if (file.endsWith('.ts')) {
              const filePath = path.join(fullPath, file);
              const content = await fs.readFile(filePath, 'utf-8');
              
              const symbolMatch = content.match(/export const (\w+_SYMBOLS)\s*=/);
              if (symbolMatch) {
                return {
                  hasSymbols: true,
                  symbolConstantName: symbolMatch[1],
                };
              }
            }
          }
        }
      }
      
      return { hasSymbols: false };
    } catch (error) {
      return { hasSymbols: false };
    }
  }

  private async scanArchitecturalPatterns(packagePath: string): Promise<{
    hasCleanArchitecture: boolean;
    hasPortsAndAdapters: boolean;
    hasCQRS: boolean;
    hasEventSourcing: boolean;
    hasDomainEvents: boolean;
    hasAntiCorruptionLayer: boolean;
  }> {
    try {
      // Check directory structure for Clean Architecture
      const hasCleanArchitecture = await this.checkCleanArchitectureStructure(packagePath);
      
      // Check for ports and adapters
      const hasPortsAndAdapters = await this.checkPortsAndAdapters(packagePath);
      
      // Check for CQRS patterns
      const hasCQRS = await this.checkCQRSPatterns(packagePath);
      
      // Check for Event Sourcing
      const hasEventSourcing = await this.checkEventSourcing(packagePath);
      
      // Check for Domain Events
      const hasDomainEvents = await this.checkDomainEvents(packagePath);
      
      // Check for Anti-Corruption Layer
      const hasAntiCorruptionLayer = await this.checkAntiCorruptionLayer(packagePath);
      
      return {
        hasCleanArchitecture,
        hasPortsAndAdapters,
        hasCQRS,
        hasEventSourcing,
        hasDomainEvents,
        hasAntiCorruptionLayer,
      };
    } catch (error) {
      return {
        hasCleanArchitecture: false,
        hasPortsAndAdapters: false,
        hasCQRS: false,
        hasEventSourcing: false,
        hasDomainEvents: false,
        hasAntiCorruptionLayer: false,
      };
    }
  }

  private async checkCleanArchitectureStructure(packagePath: string): Promise<boolean> {
    const srcPath = path.join(packagePath, 'src');
    if (!await this.directoryExists(srcPath)) return false;
    
    const requiredDirs = ['domain', 'application', 'infrastructure'];
    const existingDirs = await fs.readdir(srcPath);
    
    return requiredDirs.every(dir => existingDirs.includes(dir));
  }

  private async checkPortsAndAdapters(packagePath: string): Promise<boolean> {
    // Check for ports directory or port interfaces
    const portsPath = path.join(packagePath, 'src', 'ports');
    if (await this.directoryExists(portsPath)) return true;
    
    // Check for adapter directories
    const adaptersPath = path.join(packagePath, 'src', 'infrastructure', 'adapters');
    return await this.directoryExists(adaptersPath);
  }

  private async checkCQRSPatterns(packagePath: string): Promise<boolean> {
    // Look for command/query separation
    const applicationPath = path.join(packagePath, 'src', 'application');
    if (!await this.directoryExists(applicationPath)) return false;
    
    try {
      const files = await this.getAllFiles(applicationPath);
      return files.some(file => 
        file.includes('Command') || file.includes('Query') || 
        file.includes('UseCase') || file.includes('Handler')
      );
    } catch {
      return false;
    }
  }

  private async checkEventSourcing(packagePath: string): Promise<boolean> {
    try {
      const files = await this.getAllFiles(path.join(packagePath, 'src'));
      return files.some(file => 
        file.includes('EventStore') || file.includes('EventStream') ||
        file.includes('Aggregate') && file.includes('Event')
      );
    } catch {
      return false;
    }
  }

  private async checkDomainEvents(packagePath: string): Promise<boolean> {
    try {
      const domainPath = path.join(packagePath, 'src', 'domain');
      if (!await this.directoryExists(domainPath)) return false;
      
      const files = await this.getAllFiles(domainPath);
      return files.some(file => file.includes('Event') || file.includes('DomainEvent'));
    } catch {
      return false;
    }
  }

  private async checkAntiCorruptionLayer(packagePath: string): Promise<boolean> {
    try {
      const files = await this.getAllFiles(path.join(packagePath, 'src'));
      return files.some(file => 
        file.includes('AntiCorruption') || file.includes('Translator') ||
        file.includes('Adapter') || file.includes('Gateway')
      );
    } catch {
      return false;
    }
  }

  private extractDependencies(packageJson: any): string[] {
    const dependencies = [
      ...Object.keys(packageJson.dependencies || {}),
      ...Object.keys(packageJson.devDependencies || {}),
    ];
    
    // Filter to only include @swoft packages
    return dependencies.filter(dep => dep.startsWith('@swoft/'));
  }

  private async directoryExists(dirPath: string): Promise<boolean> {
    try {
      const stat = await fs.stat(dirPath);
      return stat.isDirectory();
    } catch {
      return false;
    }
  }

  private async isPackageDirectory(dirPath: string): Promise<boolean> {
    const packageJsonPath = path.join(dirPath, 'package.json');
    try {
      await fs.access(packageJsonPath);
      return true;
    } catch {
      return false;
    }
  }

  private async getAllFiles(dirPath: string): Promise<string[]> {
    const files: string[] = [];
    
    async function scanDir(currentPath: string) {
      const entries = await fs.readdir(currentPath);
      
      for (const entry of entries) {
        const fullPath = path.join(currentPath, entry);
        const stat = await fs.stat(fullPath);
        
        if (stat.isDirectory()) {
          await scanDir(fullPath);
        } else {
          files.push(fullPath);
        }
      }
    }
    
    await scanDir(dirPath);
    return files;
  }
}